import axios from 'axios';
import {
  getToken,
  isTokenExpired,
  isTokenExpiringSoon,
  refreshToken,
  clearAllAuthData,
  isRefreshInProgress,
  waitForRefresh,
  updateLastActivity,
} from '../utils/auth/tokenManager';

const API_BASE_URL = process.env.REACT_APP_API_URL || 'http://localhost:8080';
const MAX_RETRY_ATTEMPTS = 3;
const RETRY_DELAY_MS = 1000;
const AUTH_REQUIRED_ERROR_CODE = 'AUTH_REQUIRED';

// Public endpoints that don't require authentication
const PUBLIC_ENDPOINTS = [
  '/api/auth/login',
  '/api/auth/signup',
  '/api/auth/forgot-password',
  '/api/auth/reset-password',
  '/api/auth/verify-email',
  '/api/auth/google',
  '/api/turnstile/verify',
  '/api/public/',
];

let isRefreshing = false;
let failedRequestsQueue = [];

/**
 * Process the queue of failed requests after token refresh
 * @param {string|null} newToken - The new token, or null if refresh failed
 * @param {Error|null} error - Error if refresh failed
 */
const processQueue = (newToken, error = null) => {
  failedRequestsQueue.forEach((item) => {
    if (error) {
      item.reject(error);
    } else {
      item.resolve(newToken);
    }
  });
  failedRequestsQueue = [];
};

/**
 * Creator map stays reachable for guests, but a logged-in brand must send
 * its token or the server applies the guest card cap.
 */
const OPTIONAL_AUTH_ENDPOINTS = [
  '/api/influencer/map/pins',
  '/api/influencer/map/creators',
];

/**
 * Check if endpoint is public (doesn't require auth)
 * @param {string} url - The request URL
 * @returns {boolean} True if endpoint is public
 */
const isPublicEndpoint = (url) => {
  if (!url) return false;
  return PUBLIC_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

const isOptionalAuthEndpoint = (url) => {
  if (!url) return false;
  return OPTIONAL_AUTH_ENDPOINTS.some((endpoint) => url.includes(endpoint));
};

const isGuestAccessibleEndpoint = (url) => (
  isPublicEndpoint(url) || isOptionalAuthEndpoint(url)
);

const withAuthorization = (config, token) => ({
  ...config,
  headers: {
    ...config.headers,
    Authorization: `Bearer ${token}`,
  },
});

/**
 * Check if error is a network error
 * @param {Error} error - The error to check
 * @returns {boolean} True if network error
 */
const isNetworkError = (error) => !error.response && error.code !== 'ECONNABORTED';

const createAuthRequiredError = (config) => {
  const error = new Error('Authentication is required for this request');
  error.code = AUTH_REQUIRED_ERROR_CODE;
  error.config = config;
  return error;
};

/**
 * Calculate retry delay with exponential backoff
 * @param {number} attempt - Current attempt number (0-based)
 * @returns {number} Delay in milliseconds
 */
const getRetryDelay = (attempt) => RETRY_DELAY_MS * (2 ** attempt);

/**
 * Sleep for specified milliseconds
 * @param {number} ms - Milliseconds to sleep
 * @returns {Promise} Promise that resolves after delay
 */
const sleep = (ms) => new Promise((resolve) => { setTimeout(resolve, ms); });

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
  timeout: 30000, // 30 second timeout
});

let reduxStore = null;

/**
 * Set the Redux store for dispatching actions
 * @param {Object} store - Redux store
 */
export const setStore = (store) => {
  reduxStore = store;
};

/**
 * Get API URL
 * @returns {string} API base URL
 */
export const getApiUrl = () => API_BASE_URL;

/**
 * Dispatch logout action
 */
const dispatchLogout = () => {
  if (reduxStore) {
    // Import dynamically to avoid circular dependency
    import('./features/authSlice').then(({ logout }) => {
      reduxStore.dispatch(logout());
    });
  }
  clearAllAuthData();
};

/**
 * Refresh an expired token. Optional map requests continue as a guest when
 * refresh fails; protected requests log out and must throw AUTH_REQUIRED.
 * @returns {Promise<string|null>}
 */
const resolveExpiredToken = async (optional) => {
  if (isRefreshInProgress()) {
    try {
      const waitedToken = await waitForRefresh();
      if (waitedToken) return waitedToken;
    } catch (err) {
      console.error('Token refresh failed during request:', err);
    }
  }

  try {
    const refreshedToken = await refreshToken(API_BASE_URL);
    if (refreshedToken) return refreshedToken;
    if (!optional) dispatchLogout();
  } catch (err) {
    console.error('Token refresh failed:', err);
    if (!optional) dispatchLogout();
  }

  return null;
};

const resolveBearerToken = async (token, { optional = false } = {}) => {
  updateLastActivity();

  if (isTokenExpired(token)) {
    return resolveExpiredToken(optional);
  }

  if (isTokenExpiringSoon(token) && !isRefreshInProgress()) {
    refreshToken(API_BASE_URL).catch((err) => {
      console.error('Background token refresh failed:', err);
    });
  }

  return token;
};

api.interceptors.request.use(
  async (config) => {
    const optionalAuth = isOptionalAuthEndpoint(config.url);
    if (!optionalAuth && (!config.url || isPublicEndpoint(config.url))) {
      return config;
    }

    const token = getToken();
    const resolvedToken = token
      ? await resolveBearerToken(token, { optional: optionalAuth })
      : null;
    if (!resolvedToken) {
      if (optionalAuth) return config;
      throw createAuthRequiredError(config);
    }

    return withAuthorization(config, resolvedToken);
  },
  (error) => Promise.reject(error),
);

api.interceptors.response.use(
  (response) => response,
  async (error) => {
    const originalRequest = error.config;

    if (error.code === AUTH_REQUIRED_ERROR_CODE) {
      return Promise.reject(error);
    }

    if (isNetworkError(error)) {
      const retryCount = originalRequest.retryCount || 0;

      if (retryCount < MAX_RETRY_ATTEMPTS) {
        originalRequest.retryCount = retryCount + 1;
        const delay = getRetryDelay(retryCount);
        await sleep(delay);
        return api(originalRequest);
      }

      console.error('Max retry attempts reached for network error');
      return Promise.reject(error);
    }

    if (error.response?.status === 401) {
      // Don't retry if this is already a retry, is a queued retry, or is a public endpoint
      if (
        originalRequest.retry
        || originalRequest.isQueuedRetry
        || isGuestAccessibleEndpoint(originalRequest.url)
      ) {
        return Promise.reject(error);
      }

      // If already refreshing, queue this request
      if (isRefreshing) {
        return new Promise((resolve, reject) => {
          failedRequestsQueue.push({ resolve, reject, request: originalRequest });
        }).then((token) => {
          if (!token) {
            return Promise.reject(new Error('Token refresh failed'));
          }
          // Mark as retry so it won't trigger another refresh if it fails
          const retryConfig = {
            ...withAuthorization(originalRequest, token),
            retry: true,
            isQueuedRetry: true,
          };
          return api(retryConfig);
        }).catch((err) => Promise.reject(err));
      }

      originalRequest.retry = true;
      isRefreshing = true;

      try {
        const newToken = await refreshToken(API_BASE_URL);

        if (!newToken) {
          // Refresh failed, logout and reject all queued requests
          processQueue(null, new Error('Token refresh failed'));
          dispatchLogout();
          return Promise.reject(error);
        }

        processQueue(newToken);

        originalRequest.headers.Authorization = `Bearer ${newToken}`;
        return api(originalRequest);
      } catch (refreshError) {
        processQueue(null, refreshError);
        dispatchLogout();
        return Promise.reject(refreshError);
      } finally {
        isRefreshing = false;
      }
    }

    if (error.response?.status === 403) {
      console.error('Access forbidden:', error.response?.data?.message || 'Unknown error');

      // If it's a token-related 403, logout
      const errorMessage = error.response?.data?.message?.toLowerCase() || '';
      if (
        errorMessage.includes('token')
        || errorMessage.includes('unauthorized')
        || errorMessage.includes('expired')
      ) {
        dispatchLogout();
      }

      return Promise.reject(error);
    }

    // Handle other errors — log status only; response bodies may contain PII
    console.error('API Error:', error.response?.status);
    return Promise.reject(error);
  },
);

/**
 * Make authenticated request (convenience method)
 * @param {Object} config - Axios request config
 * @returns {Promise} Axios response promise
 */
export const authenticatedRequest = (config) => api(config);

/**
 * GET request
 * @param {string} url - Request URL
 * @param {Object} config - Additional config
 * @returns {Promise} Response promise
 */
export const get = (url, config = {}) => api.get(url, config);

/**
 * POST request
 * @param {string} url - Request URL
 * @param {Object} data - Request body
 * @param {Object} config - Additional config
 * @returns {Promise} Response promise
 */
export const post = (url, data, config = {}) => api.post(url, data, config);

/**
 * PUT request
 * @param {string} url - Request URL
 * @param {Object} data - Request body
 * @param {Object} config - Additional config
 * @returns {Promise} Response promise
 */
export const put = (url, data, config = {}) => api.put(url, data, config);

/**
 * DELETE request
 * @param {string} url - Request URL
 * @param {Object} config - Additional config
 * @returns {Promise} Response promise
 */
export const del = (url, config = {}) => api.delete(url, config);

/**
 * PATCH request
 * @param {string} url - Request URL
 * @param {Object} data - Request body
 * @param {Object} config - Additional config
 * @returns {Promise} Response promise
 */
export const patch = (url, data, config = {}) => api.patch(url, data, config);

export const CONFIG = {
  API_BASE_URL,
  MAX_RETRY_ATTEMPTS,
  RETRY_DELAY_MS,
  PUBLIC_ENDPOINTS,
};

export default api;
